'use client';

import { useState } from 'react';

interface HeroPortraitProps {
  src: string;
  srcSet?: string;
  sizes: string;
  alt: string;
  /**
   * The aspect ratio the server reserved for the box (e.g. `"1200 / 1500"` or
   * the `"4 / 5"` fallback). Used for the CLS-free initial reservation.
   */
  initialAspectRatio: string;
  /**
   * True when the row carries real `width_px`/`height_px`, so
   * `initialAspectRatio` already matches the image — the box is correct and we
   * never override it (zero CLS, the BAL-625 guarantee). False for legacy rows
   * with unknown dimensions, where the server had to guess `4 / 5`; there we
   * correct to the image's true ratio on load so `object-contain` fills the box
   * instead of letterboxing (the brown-bars bug, BAL-703).
   */
  hasIntrinsicRatio: boolean;
  className: string;
}

/**
 * The no-crop hero portrait. `object-contain` inside a box whose ratio matches
 * the image renders the whole picture with NO letterbox. When the stored ratio
 * is unknown (legacy Meteor rows, ~99% of headshots), the server can only guess
 * `4 / 5`, which letterboxes any squarer/landscape headshot — so this client
 * component reads the decoded image's natural ratio on load and corrects the
 * box. Known-dimension rows are left untouched (the box is already right).
 */
export function HeroPortrait({
  src,
  srcSet,
  sizes,
  alt,
  initialAspectRatio,
  hasIntrinsicRatio,
  className,
}: HeroPortraitProps) {
  const [aspectRatio, setAspectRatio] = useState(initialAspectRatio);

  return (
    // eslint-disable-next-line @next/next/no-img-element -- Cloudflare-resized srcSet + on-load natural-ratio box correction (BAL-703/BAL-625 no-crop, zero-CLS guarantee); next/image can't express the dynamic aspectRatio box
    <img
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      fetchPriority="high"
      decoding="async"
      style={{ aspectRatio }}
      className={className}
      onLoad={
        hasIntrinsicRatio
          ? undefined
          : (e) => {
              const img = e.currentTarget;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setAspectRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
              }
            }
      }
    />
  );
}
